<script setup lang="ts">
import { useToast } from '@solfacil/girassol'
import dayjs from 'dayjs'
import IconPlus from '~icons/girassol/add'
import IconSearch from '~icons/girassol/search'
import BankStatementPagination from '~/components/bank/BankStatementPagination.vue'
import { useWhatsApp } from '~/composables/whatsapp'
import { AuthorizationError } from '~/services-v2/payment-bff/payment-bff.errors'
import useAuth from '~/store-v2/auth/useAuth'
import useBankStatement from '~/store-v2/bank-statement/useBankStatement'
import usePartner from '~/store-v2/partners/usePartner'

const { t } = useI18n()
const { openWhatsApp } = useWhatsApp()
const isLargeScreen = useMediaQuery('(min-width: 1024px)')
const toast = useToast()
const router = useRouter()
const { data: auth } = useAuth()
const partnerId = computed(() => {
  return auth.value?.person?.partner?.id?.toString() || ''
})
const { data: partner, isLoading: isLoadingPartner } = usePartner(partnerId)
const userIsOnBlockedList = computed(() => {
  return partner.value?.blocklist?.is_blocked || false
})
const sortByOptions = [
  { value: 'RECENT', name: 'Mais recentes' },
  { value: 'OLDEST', name: 'Mais antigas' },
]
const impersonate = useImpersonate()
const impersonateStore = useImpersonateStore()

const defaultRangeDate: [Date, Date] = [dayjs().subtract(90, 'day').toDate(), new Date()]

const search = ref<string>()
const sortBy = ref(sortByOptions[0])
const rangeDate = ref<[Date, Date] | []>(defaultRangeDate)
const datePickerError = ref<string>()
const tabs = ref([
  { key: 'in-progress', label: 'Em progresso', active: true },
  { key: 'completed', label: 'Concluído', active: false },
])
const page = ref(1)
const pageSize = 10

const fetchParams = ref(getFetchParams())

const { data: bankStatement, isLoading: loading, error } = useBankStatement(fetchParams)

const hasPermission = computed(() => {
  if (error.value instanceof AuthorizationError && error.value?.code === 100) {
    return false
  }

  return true
})

const hasNextPagination = computed(() => {
  return bankStatement.value?.has_next || false
})

const shouldShowPagination = computed(() => {
  return bankStatement.value?.results && bankStatement.value.results.length > 0
})

function handleNewSimulationClick() {
  router.push(userIsOnBlockedList.value ? '/' : '/simulation/new')
}

function getFetchParams() {
  const currentTab = tabs.value.find(tab => tab.active)!.key
  const dateRange = rangeDate.value.length === 2
    ? rangeDate.value.map((date: Date) => date.toISOString().split('T')[0]).join(',')
    : undefined

  const isAdmin = auth.value?.me?.perfil === 'admin'
  const isImpersonating = impersonate.checkIsImpersonate()
  const partnerIdForQuery = isAdmin && isImpersonating
    ? impersonateStore.partnerId
    : (auth.value?.person?.partner?.id || undefined)

  return {
    customer_document: search.value || undefined,
    sort: sortBy.value.value as 'RECENT' | 'OLDEST',
    status:
      currentTab === 'in-progress'
        ? 'IN_PROGRESS'
        : ('COMPLETED' as 'IN_PROGRESS' | 'COMPLETED'),
    date_range: currentTab === 'in-progress' ? undefined : dateRange,
    partner_id: partnerIdForQuery,
    page: page.value,
    page_size: pageSize,
  }
}

function dateRangeValidation() {
  const diffInDays = dayjs(rangeDate.value[1]).diff(dayjs(rangeDate.value[0]), 'day')

  if (diffInDays > 90) {
    datePickerError.value = 'O período deve ser de no máximo 90 dias'

    return false
  }

  datePickerError.value = ''

  return true
}

function handleFormSubmit() {
  if (!dateRangeValidation())
    return

  page.value = 1
  fetchParams.value = getFetchParams()
}

function handleOnTabClick() {
  page.value = 1
  fetchParams.value = getFetchParams()
}

function handlePageChange(newPage: number) {
  page.value = newPage
  fetchParams.value = getFetchParams()
}

watch(tabs, () => {
  rangeDate.value = defaultRangeDate
  search.value = ''
  page.value = 1
})

watch(() => sortBy.value, () => {
  page.value = 1
  fetchParams.value = getFetchParams()
})

watch(() => error.value, (error) => {
  if (error !== null && !bankStatement) {
    toast.createErrorToast({
      title: 'Erro ao buscar extrato bancário',
      description: 'Não foi possível buscar o extrato. Tente novamente mais tarde.',
    })
  }
})

watch(partner, (newPartner) => {
  if (newPartner?.blocklist?.is_blocked) {
    router.push({ path: '/projects' })
  }
})
</script>

<template>
  <div class="initial-page-container">
    <SolAlert
      v-if="userIsOnBlockedList"
      id="error-feedback"
      :title="t('blocked_list.alert.title')"
      feedback="error"
      :action="t('blocked_list.alert.action')"
      class="mb-4xs"
      @click="openWhatsApp('5511912386374', t('blocked_list.whatsapp.message'))"
    >
      {{ t('blocked_list.alert.description') }}
    </SolAlert>

    <div class="page-heading">
      <div>
        <h2 class="fonts-heading-h2">
          Extrato
        </h2>
      </div>

      <SolButton
        v-if="auth?.person.partner?.permission.financing"
        id="new-simulation"
        :disabled="userIsOnBlockedList"
        :loading="isLoadingPartner"
        class="whitespace-nowrap"
        @click="handleNewSimulationClick"
      >
        <template #icon-left>
          <IconPlus />
        </template>
        {{ t('home.newSimulation') }}
      </SolButton>
    </div>

    <div v-if="hasPermission" class="bank-statement-card">
      <p class="fonts-subtitle-small text-neutral-low-light">
        Confira as entradas e saídas da sua conta vinculada com a Solfácil em
        parceria com a QI Tech. As entradas são as transferências para a sua
        conta vinculada (valor financiado líquido e comissões), e as saídas são
        as transferências da sua conta vinculada para a sua conta pessoal e/ou
        para o fornecedor.
      </p>
      <form class="filter-wrapper" @submit.prevent="handleFormSubmit">
        <SolInputText
          id="customer-search" v-model="search" name="customer-search" class="customer-search-input"
          placeholder="Busque por Nome, CPF ou CNPJ"
        >
          <template v-if="!isLargeScreen" #icon>
            <IconSearch />
          </template>
        </SolInputText>

        <SolDatePicker
          v-if="tabs.find(tab => tab.active)!.key === 'completed'" id="range-date-picker" v-model="rangeDate"
          :class="isLargeScreen ? 'w-68' : 'w-full'" name="range-date-picker" placeholder="Selecione o período" :range="true" label="" :error="datePickerError"
        />

        <SolButton id="customer-search-button" type="submit" variant="primary" size="medium">
          Buscar
        </SolButton>
      </form>

      <section class="w-full">
        <div class="bank-statement-heading">
          <h3 class="fonts-heading-h3 mb-2xs">
            Últimas transferências
          </h3>

          <div class="flex gap-2 items-baseline justify-end mb-2xs">
            <label for="sort-by" class="text-neutral-low-dark">
              Ordenar por:
            </label>
            <SolSelect id="sort-by" v-model:selected="sortBy" name="sort-by" :options="sortByOptions" size="small" />
          </div>
        </div>

        <SolTabs id="bank-statement-tabs" v-model:tab-list="tabs" @click.stop="handleOnTabClick">
          <template #tab:in-progress>
            <BankStatementTab v-if="bankStatement" :bank-statement="bankStatement" />
            <div class="flex justify-center">
              <Loading v-if="loading" size="small" />
              <SolEmptyState
                v-else-if="!loading && (!bankStatement || bankStatement?.results.length === 0)"
                id="not-found-in-progress" variant="empty-list" title="Nenhuma transferência realizada"
                subtitle="Ainda não há nenhuma transferência registrada neste extrato. Faça sua primeira formalização para ver o registro das transações."
              />
            </div>
          </template>

          <template #tab:completed>
            <BankStatementTab v-if="bankStatement" :bank-statement="bankStatement" />
            <div class="flex justify-center">
              <Loading v-if="loading" size="small" />
              <SolEmptyState
                v-else-if="!loading && (!bankStatement || bankStatement?.results.length === 0)"
                id="not-found-completed" variant="empty-list" title="Nenhuma transferência realizada"
                subtitle="Ainda não há nenhuma transferência registrada neste extrato. Faça sua primeira formalização para ver o registro das transações."
              />
            </div>
          </template>
        </SolTabs>

        <BankStatementPagination
          v-if="shouldShowPagination"
          :page="page"
          :has-next-pagination="hasNextPagination"
          @change-page="handlePageChange"
        />
      </section>
    </div>
    <div v-else-if="!hasPermission && loading" class="flex items-center justify-center w-full">
      <Loading size="small" />
    </div>
    <div v-else class="flex items-center justify-center w-full">
      <SolEmptyState
        id="no-permission" variant="no-content" title="Você não tem acesso ao extrato"
        subtitle="Apenas usuários como perfil gerencial tem acesso ao movimento bancário. Solicite ao administrador da sua conta o seu acesso."
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.initial-page-container {
  @apply flex flex-col py-2xs px-4xs gap-4 w-full;

  @screen md:system {
    @apply pt-2xs pb-sm px-2xs;
  }

  .page-heading {
    @apply flex flex-col gap-4 w-full;

    @screen md:system {
      @apply flex-row items-center justify-between;
    }
  }

  .bank-statement-card {
    @apply flex flex-col p-2xs gap-4 w-full;
    @apply bg-neutral-high-pure border border-neutral-high-medium rounded-2xl;

    .bank-statement-heading {
      @apply flex flex-col;

      @screen md:system {
        @apply flex-row justify-between;
      }
    }

    form.filter-wrapper {
      @apply mb-3xs w-full;
      @apply py-2xs px-4xs;

      @apply flex flex-col gap-2;

      @apply bg-neutral-high-light;
      @apply rounded-lg;

      @screen md:system {
        @apply flex-row;
      }

      .customer-search-input {
        @apply w-full;

        @screen md:system {
          @apply w-80;
        }
      }
    }
  }
}
</style>
